import { lazy, Suspense, useEffect, useState } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home"; // page d'atterrissage principale : eager pour un LCP immédiat
import { OrgJsonLd } from "@/components/JsonLd";
import { AdminAuthProvider } from "@/admin/AuthContext";
import { initAds } from "@/lib/ads";

// ---------------------------------------------------------------------------
// SEO / Performance : code-splitting par route (React.lazy).
// Seuls Home + Header/Footer/CookieBanner restent dans le bundle initial ;
// l'admin (recharts, éditeurs…), leaflet, react-markdown et toutes les pages
// secondaires sont chargés à la demande. Ne PAS réintroduire d'import statique.
// ---------------------------------------------------------------------------
const Concept = lazy(() => import("@/pages/Concept"));
const Approche = lazy(() => import("@/pages/Approche"));
const Investisseurs = lazy(() => import("@/pages/Investisseurs"));
const APropos = lazy(() => import("@/pages/APropos"));
const Projets = lazy(() => import("@/pages/Projets"));
const ProjetDetail = lazy(() => import("@/pages/ProjetDetail"));
const Opportunites = lazy(() => import("@/pages/Opportunites"));
const NovaTV = lazy(() => import("@/pages/NovaTV"));
const NovaTVArticle = lazy(() => import("@/pages/NovaTVArticle"));
const Accompagnement = lazy(() => import("@/pages/Accompagnement"));
const Terrain = lazy(() => import("@/pages/Terrain"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const NewsletterUnsubscribe = lazy(() => import("@/pages/NewsletterUnsubscribe"));
const Entreprendre = lazy(() => import("@/pages/Entreprendre"));
const ConseilImmobilier = lazy(() => import("@/pages/ConseilImmobilier"));
const LifeMatch = lazy(() => import("@/pages/LifeMatch"));
const LifeMatchTest = lazy(() => import("@/pages/LifeMatchTest"));
const LifeMatchResult = lazy(() => import("@/pages/LifeMatchResult"));
const PrivateNetwork = lazy(() => import("@/pages/PrivateNetwork"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Suivi = lazy(() => import("@/pages/Suivi"));
const PrestationConfirm = lazy(() => import("@/pages/PrestationConfirm"));
const AssistantChatLazy = lazy(() => import("@/components/AssistantChat").then((m) => ({ default: m.AssistantChat })));
const AdminLogin = lazy(() => import("@/admin/Login"));
const AdminLayout = lazy(() => import("@/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/admin/Dashboard"));
const Leads = lazy(() => import("@/admin/Leads"));
const ContentList = lazy(() => import("@/admin/ContentList"));
const StudioIA = lazy(() => import("@/admin/StudioIA"));
const SocialComposer = lazy(() => import("@/admin/SocialComposer"));
const Propositions = lazy(() => import("@/admin/Propositions"));
const LifeMatchAdmin = lazy(() => import("@/admin/LifeMatchAdmin"));
const NewsletterAdmin = lazy(() => import("@/admin/Newsletter"));
const ServicesCatalogue = lazy(() => import("@/admin/services/Catalogue"));
const ServicesPrestataires = lazy(() => import("@/admin/services/Prestataires"));
const ServicesCommandes = lazy(() => import("@/admin/services/Commandes"));
const ServicesPlanning = lazy(() => import("@/admin/services/Planning"));
const ServicesRentabilite = lazy(() => import("@/admin/services/Rentabilite"));
const EntreprendreAdmin = lazy(() => import("@/admin/Entreprendre"));
const ConseilAdmin = lazy(() => import("@/admin/Conseil"));
const MediasAdmin = lazy(() => import("@/admin/Medias"));
const ProjectForm = lazy(() => import("@/admin/forms/ProjectForm"));
const OpportunityForm = lazy(() => import("@/admin/forms/OpportunityForm"));
const SiteSettings = lazy(() => import("@/admin/SiteSettings"));
const ArticleForm = lazy(() => import("@/admin/forms/ArticleForm"));
const DossierList = lazy(() => import("@/admin/dossiers/DossierList"));
const DossierForm = lazy(() => import("@/admin/dossiers/DossierForm"));
const Biens = lazy(() => import("@/admin/business/Biens"));
const BienDetail = lazy(() => import("@/admin/business/BienDetail"));
const Ventes = lazy(() => import("@/admin/business/Ventes"));
const SaleDetail = lazy(() => import("@/admin/business/SaleDetail"));
const Locations = lazy(() => import("@/admin/business/Locations"));
const LeaseDetail = lazy(() => import("@/admin/business/LeaseDetail"));
const Depenses = lazy(() => import("@/admin/business/Depenses"));
const Contacts = lazy(() => import("@/admin/business/Contacts"));
const Tresorerie = lazy(() => import("@/admin/business/Tresorerie"));
const Exports = lazy(() => import("@/admin/business/Exports"));
const Clients = lazy(() => import("@/admin/crm/Clients"));
const ClientDetail = lazy(() => import("@/admin/crm/ClientDetail"));
const Invoices = lazy(() => import("@/admin/invoices/Invoices"));
const Comptabilite = lazy(() => import("@/admin/compta/Comptabilite"));

// L'assistant flottant n'est pas critique pour le rendu : son chunk n'est
// téléchargé qu'après la première interaction (scroll/tap) ou 3,5 s d'inactivité.
const DeferredAssistant = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const activate = () => setReady(true);
    const timer = setTimeout(activate, 3500);
    window.addEventListener("scroll", activate, { once: true, passive: true });
    window.addEventListener("pointerdown", activate, { once: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", activate);
      window.removeEventListener("pointerdown", activate);
    };
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <AssistantChatLazy />
    </Suspense>
  );
};

const ScrollManager = () => {
  // location.key change à CHAQUE navigation (même vers la même URL) → un re-clic sur "Packs & tarifs" refonctionne
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let cancelled = false;
    const timers = [];

    // L'élément est-il bien positionné ? (scrollIntoView respecte scroll-margin-top, ex. scroll-mt-24 = 96px)
    const isAligned = (el) => {
      const expected = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      return Math.abs(el.getBoundingClientRect().top - expected) <= 60;
    };

    // 1) Attend que l'élément soit monté (poll 100ms, max 3s) puis scrolle
    let tries = 0;
    const scrollNow = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    const firstEl = document.getElementById(id);
    const onFound = () => {
      scrollNow();
      // 2) Passes correctives : les images/sections au-dessus peuvent décaler la mise en page après coup.
      //    On ne re-scrolle que si l'ancre a dérivé, pour ne jamais arracher un scroll manuel de l'utilisateur.
      [700, 1500].forEach((ms) =>
        timers.push(
          setTimeout(() => {
            if (cancelled) return;
            const el = document.getElementById(id);
            if (el && !isAligned(el)) scrollNow();
          }, ms)
        )
      );
    };
    if (firstEl) {
      onFound();
    } else {
      const poll = setInterval(() => {
        tries += 1;
        const el = document.getElementById(id);
        if (el) {
          clearInterval(poll);
          if (!cancelled) onFound();
        } else if (tries > 30) {
          clearInterval(poll);
        }
      }, 100);
      timers.push({ clear: () => clearInterval(poll) });
    }

    return () => {
      cancelled = true;
      timers.forEach((tm) => (typeof tm === "number" ? clearTimeout(tm) : tm.clear()));
    };
  }, [pathname, hash, key]);
  return null;
};

const PublicLayout = () => (
  <>
    <Header />
    <main>
      <Outlet />
    </main>
    <Footer />
    <CookieBanner />
    <DeferredAssistant />
    <OrgJsonLd />
  </>
);

function App() {
  useEffect(() => {
    // Snapshot racine prerendu : sur une URL inconnue, un script inline masque le
    // contenu accueil (html[data-nf]) — React ayant repris la main, on réaffiche.
    document.documentElement.removeAttribute("data-nf");
    // Google Ads : balise chargée uniquement si consentement cookies accepté
    // (immédiatement si déjà donné, sinon à l'acceptation via la bannière).
    initAds();
  }, []);
  return (
    <BrowserRouter>
      <ScrollManager />
      <Suspense fallback={null}>
        <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/concept" element={<Concept />} />
          <Route path="/approche" element={<Approche />} />
          {/* R1/R2 — anciennes URLs fusionnées : redirections permanentes côté SPA (le sitemap ne les référence plus) */}
          <Route path="/methode" element={<Navigate to="/approche" replace />} />
          <Route path="/services" element={<Navigate to="/approche" replace />} />
          <Route path="/ecosysteme" element={<Navigate to="/private-network#ecosysteme" replace />} />
          <Route path="/investisseurs" element={<Investisseurs />} />
          <Route path="/a-propos" element={<APropos />} />
          <Route path="/projets" element={<Projets />} />
          <Route path="/projets/:slug" element={<ProjetDetail />} />
          <Route path="/opportunites" element={<Opportunites />} />
          <Route path="/novatv" element={<NovaTV />} />
          <Route path="/novatv/:slug" element={<NovaTVArticle />} />
          <Route path="/j-ai-un-terrain" element={<Terrain />} />
          <Route path="/accompagnement" element={<Accompagnement />} />
          <Route path="/entreprendre" element={<Entreprendre />} />
          <Route path="/conseil-projets-immobiliers" element={<ConseilImmobilier />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/lifematch" element={<LifeMatch />} />
          <Route path="/lifematch/test" element={<LifeMatchTest />} />
          <Route path="/lifematch/resultat/:token" element={<LifeMatchResult />} />
          <Route path="/private-network" element={<PrivateNetwork />} />
          <Route path="/mentions-legales" element={<Legal pageKey="mentions" />} />
          <Route path="/cgu" element={<Legal pageKey="cgu" />} />
          <Route path="/politique-de-confidentialite" element={<Legal pageKey="confidentialite" />} />
          <Route path="/politique-cookies" element={<Legal pageKey="cookies" />} />
          <Route path="/newsletter/desinscription" element={<NewsletterUnsubscribe />} />
          {/* Catch-all : page 404 propre (noindex, sans canonical). NB : le statut HTTP
              reste 200 côté hébergeur statique — limitation d'infra documentée. */}
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/suivi/:token" element={<Suivi />} />
        <Route path="/prestation/:token" element={<PrestationConfirm />} />
        <Route path="/admin" element={<AdminAuthProvider />}>
          <Route path="login" element={<AdminLogin />} />
          <Route element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="biens" element={<Biens />} />
            <Route path="biens/:id" element={<BienDetail />} />
            <Route path="ventes" element={<Ventes />} />
            <Route path="ventes/:id" element={<SaleDetail />} />
            <Route path="locations" element={<Locations />} />
            <Route path="locations/:id" element={<LeaseDetail />} />
            <Route path="depenses" element={<Depenses />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="tresorerie" element={<Tresorerie />} />
            <Route path="comptabilite" element={<Comptabilite />} />
            <Route path="exports" element={<Exports />} />
            <Route path="leads" element={<Leads />} />
            <Route path="entreprendre" element={<EntreprendreAdmin />} />
            <Route path="conseil" element={<ConseilAdmin />} />
            <Route path="medias" element={<MediasAdmin />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="factures" element={<Invoices />} />
            <Route path="dossiers" element={<DossierList />} />
            <Route path="dossiers/nouveau" element={<DossierForm />} />
            <Route path="dossiers/:id" element={<DossierForm />} />
            <Route path="projets" element={<ContentList resource="projects" />} />
            <Route path="projets/nouveau" element={<ProjectForm />} />
            <Route path="projets/:id" element={<ProjectForm />} />
            <Route path="opportunites" element={<ContentList resource="opportunities" />} />
            <Route path="reglages" element={<SiteSettings />} />
            <Route path="opportunites/nouveau" element={<OpportunityForm />} />
            <Route path="opportunites/:id" element={<OpportunityForm />} />
            <Route path="novatv" element={<ContentList resource="novatv" />} />
            <Route path="novatv/studio" element={<StudioIA />} />
            <Route path="novatv/propositions" element={<Propositions />} />
            <Route path="novatv/nouveau" element={<ArticleForm />} />
            <Route path="novatv/:id" element={<ArticleForm />} />
            <Route path="reseaux-sociaux" element={<SocialComposer />} />
            <Route path="lifematch" element={<LifeMatchAdmin />} />
            <Route path="newsletter" element={<NewsletterAdmin />} />
            <Route path="services/catalogue" element={<ServicesCatalogue />} />
            <Route path="services/prestataires" element={<ServicesPrestataires />} />
            <Route path="services/commandes" element={<ServicesCommandes />} />
            <Route path="services/planning" element={<ServicesPlanning />} />
            <Route path="services/rentabilite" element={<ServicesRentabilite />} />
          </Route>
        </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
